<template>
    <zen-message-modal
        :isOpen="isOpen"
        title="Delete Account?"
        variant="destructive"
    >
        <p class="text-taupe-700">
            Are you sure you want to delete your account? This action is
            permanent and cannot be undone.
        </p>
        <div
            class="mt-3 space-y-2 text-sm text-taupe-700"
            data-testid="delete-account-consequences"
        >
            <p v-if="loading" data-testid="delete-account-loading">
                Checking what happens to your subscription…
            </p>
            <p
                v-else-if="refusal"
                class="font-medium text-red-700"
                role="alert"
                data-testid="delete-account-refusal"
            >
                {{ refusal }}
            </p>
            <template v-else-if="preview">
                <p data-testid="delete-account-subscription">
                    Your subscription ends right away.
                </p>
                <p v-if="forfeitedCredit" data-testid="delete-account-credit">
                    You have {{ forfeitedCredit }} in credit. It is forfeited
                    when the account is deleted, and it is not refunded.
                </p>
                <p v-if="itemsDropped" data-testid="delete-account-dropped">
                    Charges and credits not yet invoiced are dropped.
                </p>
            </template>
        </div>
        <template v-slot:buttons>
            <zen-button
                type="button"
                @click="$emit('confirm')"
                variant="primary-danger"
                :processing="processing"
                :disabled="!canConfirm"
            >
                Delete Account
            </zen-button>
            <zen-button
                type="button"
                @click="$emit('close')"
                variant="secondary"
                :disabled="processing"
            >
                Cancel
            </zen-button>
        </template>
    </zen-message-modal>
</template>

<script>
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { formatMoney } from '@/helpers/billing-format'
import { unpaidBalanceMessage } from '@/helpers/delete-account'

const COULD_NOT_CHECK =
    'We could not check your account just now. Close this and try again.'

export default {
    name: 'DeleteAccountModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        processing: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'confirm'],
    data() {
        return {
            loading: false,
            preview: null,
            refusal: null,
            // Which open the answer being waited for belongs to, so one that
            // arrives after the modal closed (or reopened) is dropped.
            loadId: 0,
        }
    },
    computed: {
        // The delete is immediate and forfeits credit, and the API says what it
        // would do (and refuses it over an unpaid invoice) before the owner
        // confirms. So deleting waits for the preview and is off while the API
        // refuses it.
        canConfirm() {
            return !this.loading && !this.refusal && this.preview !== null
        },
        // "$5.00" or "$5.00 and €3.00": credit by currency, in cents.
        forfeitedCredit() {
            const credit = this.preview?.proposed?.forfeited_credit ?? {}

            return Object.entries(credit)
                .filter(([, cents]) => cents > 0)
                .map(([currency, cents]) => formatMoney(cents, currency))
                .join(' and ')
        },
        itemsDropped() {
            return (this.preview?.proposed?.pending_items_dropped ?? 0) > 0
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(open) {
                if (open) {
                    this.loadPreview()
                } else {
                    this.reset()
                }
            },
        },
    },
    methods: {
        reset() {
            this.loadId += 1
            this.loading = false
            this.preview = null
            this.refusal = null
        },
        loadPreview() {
            const loadId = ++this.loadId
            this.loading = true
            this.preview = null
            this.refusal = null

            return ProfileAccountBillingService.deletionPreview()
                .then((response) => {
                    if (loadId === this.loadId) {
                        this.preview = response.data
                    }
                })
                .catch((error) => {
                    if (loadId === this.loadId) {
                        // The api client has already turned the API's message
                        // (or a network failure) into the error's own message.
                        this.refusal =
                            unpaidBalanceMessage(error) ||
                            error.message ||
                            COULD_NOT_CHECK
                    }
                })
                .finally(() => {
                    if (loadId === this.loadId) {
                        this.loading = false
                    }
                })
        },
    },
}
</script>
